import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {IconMapPin} from '@tabler/icons-react';
import React, {useMemo} from 'react';
import type {InspectionScopeDeviceResponse} from 'src/api/ProactApiClient';
import {Textbox} from 'src/design/atoms/Textbox';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {BrowserLayout} from 'src/design/organisms/BrowserLayout';
import {
  ALL_LOCATIONS_NODE_ID,
  buildDeviceGroupTree,
  filterScopeDevices,
  findDeviceGroupNode,
} from 'src/app/inspections/deviceRollup';
import {LocationTree} from 'src/app/inspections/LocationTree';
import {InspectionDeviceTable} from './InspectionDeviceTable';

export interface DevicesTabPanelProps {
  devices: InspectionScopeDeviceResponse[];
  canRecord: boolean;
  onEnterDevice: (device: InspectionScopeDeviceResponse) => void;
  search: string;
  onSearchChange: (value: string) => void;
  nodeId: string;
  onNodeChange: (value: string) => void;
  showOnlyRemaining: boolean;
  onShowOnlyRemainingChange: (value: boolean) => void;
}

// Search/tree/remaining are display predicates only — the device list itself is not refetched.
export const DevicesTabPanel: React.FC<DevicesTabPanelProps> = ({
  devices,
  canRecord,
  onEnterDevice,
  search,
  onSearchChange,
  nodeId,
  onNodeChange,
  showOnlyRemaining,
  onShowOnlyRemainingChange,
}) => {
  const tree = useMemo(() => buildDeviceGroupTree(devices), [devices]);

  const selectedNode =
    nodeId === ALL_LOCATIONS_NODE_ID
      ? undefined
      : findDeviceGroupNode(tree, nodeId);
  const selectedNodeLabel = selectedNode?.label ?? 'All locations';
  const selectedNodeCount = selectedNode?.counts.total ?? devices.length;

  const filteredDevices = useMemo(
    () => filterScopeDevices(devices, {nodeId, search, showOnlyRemaining}),
    [devices, nodeId, showOnlyRemaining, search]
  );

  if (devices.length === 0) {
    return (
      <NoItemsPanel noItemsText="No devices in scope for this inspection." />
    );
  }

  const treePane = (
    <LocationTree
      tree={tree}
      totalCount={devices.length}
      selectedNodeId={nodeId}
      onSelectNode={onNodeChange}
    />
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Textbox
          ariaLabel="Search devices"
          placeholder="Search by name or serial"
          value={search}
          onChangeCallback={event => onSearchChange(event.currentTarget.value)}
        />
        <Switch
          size="sm"
          label="Show only remaining"
          checked={showOnlyRemaining}
          onChange={event =>
            onShowOnlyRemainingChange(event.currentTarget.checked)
          }
        />
      </Group>

      <Text size="xs" c="dimmed">
        {filteredDevices.length} shown
      </Text>

      <BrowserLayout
        pane={treePane}
        paneTitle="Locations"
        paneWidth={280}
        chipIcon={<IconMapPin size={14} />}
        selectionLabel={`${selectedNodeLabel} · ${selectedNodeCount}`}
        selectionKey={nodeId}>
        <InspectionDeviceTable
          devices={filteredDevices}
          canRecord={canRecord}
          onEnterDevice={onEnterDevice}
        />
      </BrowserLayout>
    </Stack>
  );
};
